/**
 * WhiteLine Component
 *
 * Draws a white line that continues from where the cyan OVNI line ends,
 * connecting to the train element in the gradient section.
 *
 * ## Architecture
 * - The white line starts at the exact point where the cyan line ends
 * - It goes down vertically, then curves right to connect with the train
 * - The SVG is positioned inside .gradient-curve-top
 *
 * ## Coordinate System
 * - The cyan line ends at X=0 in wrapper coords (left edge of #ovniLineWrapper)
 * - The wrapper has left: 8.3% offset, so we translate to curveTop coordinates
 * - The Y position is calculated using the ellipse formula to match the curve edge
 *
 * ## Ellipse Calculation
 * The CSS ::before element creates an ellipse with border-radius: 50% 50% 0 0:
 * - radiusX = (110% * containerWidth) / 2
 * - radiusY = 100px / 2 = 50px
 *
 * The ellipse edge Y from top: radiusY - (radiusY * sqrt(1 - ((x - cx) / rx)^2))
 */

/**
 * Controls the white line SVG that connects the cyan line to the train.
 *
 * @example
 * ```typescript
 * import { initWhiteLine } from './components/WhiteLine';
 *
 * document.addEventListener('DOMContentLoaded', () => {
 *   const whiteLine = initWhiteLine();
 *   // Later, to cleanup:
 *   // whiteLine?.destroy();
 * });
 * ```
 */
export class WhiteLine {
  private svg: SVGSVGElement | null;
  private path: SVGPathElement | null;
  private train: HTMLElement | null;
  private curveTop: HTMLElement | null;

  private resizeTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.svg = document.getElementById('whiteLineSvg') as SVGSVGElement | null;
    this.path = document.getElementById('whiteLinePath') as SVGPathElement | null;
    this.train = document.getElementById('curveTrain');
    this.curveTop = document.querySelector('.gradient-curve-top');
  }

  /**
   * Initializes the component if all required DOM elements exist.
   * Generates the initial path and binds resize events.
   */
  public init(): void {
    if (!this.svg || !this.path || !this.train || !this.curveTop) {
      return;
    }

    this.generatePath();
    this.bindEvents();

    // Show with fade after calculation
    requestAnimationFrame(() => {
      this.svg?.classList.add('is-ready');
    });
  }

  /** Binds resize event listener */
  private bindEvents(): void {
    window.addEventListener('resize', this.handleResize, { passive: true });

    // Recalculate when fonts are loaded (text dimensions may change)
    void document.fonts.ready.then(() => {
      this.generatePath();
    });

    // Also on window load to be safe
    window.addEventListener('load', () => {
      this.generatePath();
    });
  }

  /** Handles resize events with 50ms debounce to regenerate path */
  private handleResize = (): void => {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }

    this.resizeTimeout = setTimeout(() => {
      this.generatePath();
    }, 50);
  };

  /**
   * Calculates the Y position on the ellipse edge at a given X coordinate.
   *
   * For an ellipse created with CSS border-radius: 50% 50% 0 0:
   * - The curve is at the TOP of the element
   * - yFromBottom = radiusY * sqrt(1 - ((x - centerX) / radiusX)^2)
   *
   * @param x - The X coordinate to calculate Y for
   * @param ellipseCenterX - The horizontal center of the ellipse
   * @param ellipseRadiusX - The horizontal radius (width / 2)
   * @param ellipseRadiusY - The vertical radius (height / 2, NOT full height!)
   * @returns The Y distance from the bottom of the ellipse
   */
  private getEllipseYFromBottom(
    x: number,
    ellipseCenterX: number,
    ellipseRadiusX: number,
    ellipseRadiusY: number
  ): number {
    const xRelative = x - ellipseCenterX;
    const normalized = xRelative / ellipseRadiusX;
    const clamped = Math.max(-1, Math.min(1, normalized));
    return ellipseRadiusY * Math.sqrt(1 - clamped * clamped);
  }

  /**
   * Generates the SVG path from the ellipse edge to the train.
   *
   * The path:
   * 1. Starts at the ellipse edge (where cyan line ends)
   * 2. Goes down vertically
   * 3. Curves right with a quadratic bezier
   * 4. Continues horizontally to the train
   */
  private generatePath(): void {
    if (!this.svg || !this.path || !this.train || !this.curveTop) {
      return;
    }

    const curveRect = this.curveTop.getBoundingClientRect();
    const trainRect = this.train.getBoundingClientRect();

    const curveTopWidth = curveRect.width;

    // Ellipse properties from CSS ::before
    // ::before has width: 110%, height varies by breakpoint, border-radius: 50% 50% 0 0
    // Match the CSS breakpoints in gradient-wrapper.css
    let ellipseBeforeHeight = 100; // Desktop default
    if (window.innerWidth <= 480) {
      ellipseBeforeHeight = 50; // Very small mobile
    } else if (window.innerWidth <= 768) {
      ellipseBeforeHeight = 60; // Mobile/tablet
    }
    
    const ellipseWidth = curveTopWidth * 1.1;
    const ellipseCenterX = curveTopWidth / 2;
    const ellipseRadiusX = ellipseWidth / 2;
    // CSS border-radius: 50% uses HALF the height as vertical radius
    const ellipseRadiusY = ellipseBeforeHeight / 2;

    // Get the cyan line end point to determine X position
    const cyanPath = document.getElementById('ovniPath') as SVGPathElement | null;
    const ovniWrapper = document.getElementById('ovniLineWrapper');

    // Fallback: estimate based on wrapper offset (8.3%)
    let lineEndX = curveTopWidth * 0.083;

    if (cyanPath && ovniWrapper) {
      const wrapperRect = ovniWrapper.getBoundingClientRect();
      const cyanPathLength = cyanPath.getTotalLength();
      const endPoint = cyanPath.getPointAtLength(cyanPathLength);

      // Translate from wrapper coords to curveTop coords
      const wrapperLeftOffset = wrapperRect.left - curveRect.left;
      lineEndX = wrapperLeftOffset + endPoint.x;
    }

    // Calculate ellipse edge Y at the line's X position
    const ellipseYFromBottom = this.getEllipseYFromBottom(
      lineEndX,
      ellipseCenterX,
      ellipseRadiusX,
      ellipseRadiusY
    );

    // Convert to Y from top (curve is at top of ::before)
    const ellipseYFromTop = ellipseRadiusY - ellipseYFromBottom;
    const ellipseEdgeY = ellipseYFromTop;

    // Position SVG to start at the ellipse edge
    const svgTop = ellipseEdgeY;

    // Calculate train position relative to curveTop
    // Line ends at the RIGHT edge of train (trainRight) and at the BOTTOM (trainBottomY)
    const trainRight = trainRect.right - curveRect.left;
    const trainBottomY = trainRect.top - curveRect.top + trainRect.height;

    // SVG height: from ellipse edge to the train bottom (with padding)
    const svgHeight = trainBottomY - svgTop + 50;

    // Set SVG position and dimensions
    this.svg.style.top = `${svgTop}px`;
    this.svg.style.height = `${svgHeight}px`;
    this.svg.setAttribute('width', String(curveTopWidth));
    this.svg.setAttribute('height', String(svgHeight));
    this.svg.setAttribute('viewBox', `0 0 ${curveTopWidth} ${svgHeight}`);

    // Path coordinates (SVG local coords, y=0 is at ellipse edge)
    // Add 3px offset so train sits slightly above the line
    const startX = lineEndX;
    const startY = 0;
    const endX = trainRight;
    const endY = trainBottomY - svgTop + 3;

    // Vertical segment ends 30px before the curve
    const verticalEndY = endY - 30;

    // Build path: vertical line, quadratic curve, horizontal line
    const pathD = [
      `M ${startX} ${startY}`,
      `L ${startX} ${verticalEndY}`,
      `Q ${startX} ${endY}, ${startX + 50} ${endY}`,
      `L ${endX} ${endY}`,
    ].join(' ');

    this.path.setAttribute('d', pathD);
  }

  /**
   * Cleans up event listeners and timeouts.
   * Call this when removing the component from the page.
   */
  public destroy(): void {
    window.removeEventListener('resize', this.handleResize);

    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }
}

/**
 * Factory function to create and initialize a WhiteLine instance.
 *
 * @returns The initialized WhiteLine instance, or null if initialization failed
 */
export function initWhiteLine(): WhiteLine | null {
  const whiteLine = new WhiteLine();
  whiteLine.init();
  return whiteLine;
}
